import { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { aiAPI } from '../../services/api';
import toast from '../../services/toast';

function flattenCatalog(data) {
  if (!data) return [];
  return [
    ...(data.projects || []).map((p) => ({ ...p, section: 'Projects' })),
    ...(data.repositories || []).map((r) => ({ ...r, section: 'Repositories' })),
    ...(data.streams || []).map((s) => ({ ...s, section: 'Streams' })),
    ...(data.members || []).map((m) => ({ ...m, section: 'Members' })),
  ];
}

export default function VantiScopePicker({ open, onClose, onSelect }: any) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const abortRef = useRef<any>(null);

  const load = useCallback(async (q) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const data = await aiAPI.scopeCatalog({ q, limit: 40 }, controller.signal);
      if (abortRef.current === controller) {
        setItems(flattenCatalog(data));
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      toast.error((e as Error).message || 'Failed to load scope list');
      setItems([]);
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(query), 250);
    return () => clearTimeout(t);
  }, [open, query, load]);

  useEffect(() => {
    if (!open) {
      if (abortRef.current) abortRef.current.abort();
      return;
    }
    setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open && abortRef.current) {
      abortRef.current.abort();
    }
  }, [open]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Choose context"
      contentClassName="max-w-md"
      bodyClassName="flex max-h-[min(70vh,28rem)] flex-col gap-0 p-0"
    >
      <div className="border-b border-dark-border px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects, repos, streams…"
            className="w-full rounded-lg border border-dark-border bg-dark-surface py-2 pl-9 pr-3 text-sm text-slate-100"
            autoFocus
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain p-2">
        {loading ? (
          <p className="px-2 py-4 text-sm text-slate-400">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-2 py-4 text-sm text-slate-400">No matches.</p>
        ) : (
          items.map((item) => (
            <button
              key={`${item.kind}-${item.id}`}
              type="button"
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-vivid-purple/20"
              onClick={() => {
                onSelect(item);
                onClose();
              }}
            >
              <span className="block text-xs text-slate-400">{item.section}</span>
              <span className="font-medium">{item.label}</span>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
